"use client";

import React from 'react';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  MoreHorizontal,
  Pin,
  Eye,
  Pencil,
  Trash2,
  Clock,
  LucideIcon
} from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export interface NoteCardData {
  id: number | string;
  content: string;
  isPinned?: boolean;
  createdAt: string;
  updatedAt?: string;
  author?: {
    name: string;
    avatarUrl?: string;
  };
  relatedEntity?: {
    type: string;
    name: string;
    icon?: LucideIcon;
    color?: string;
    href?: string;
  };
}

interface NoteCardProps {
  note: NoteCardData;
  onPin?: () => void;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
  accentColor?: string;
}

const getInitials = (name: string) => {
  return name.split(' ').map(word => word[0]).join('').toUpperCase().slice(0, 2);
};

const formatRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Ahora';
  if (diffMins < 60) return `Hace ${diffMins}m`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays < 7) return `Hace ${diffDays}d`;
  
  return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
};

export function NoteCard({ 
  note, 
  onPin, 
  onView, 
  onEdit, 
  onDelete,
  className,
  accentColor = 'var(--agent-accent)'
}: NoteCardProps) {
  const isEdited = note.updatedAt && note.updatedAt !== note.createdAt;
  const RelatedIcon = note.relatedEntity?.icon;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.2 }}
      className={cn("break-inside-avoid mb-4", className)}
    >
      <div 
        className={cn(
          "bg-white/80 backdrop-blur-sm rounded-2xl shadow-md hover:shadow-xl",
          "transition-all duration-300 hover:scale-[1.02] cursor-pointer",
          "border border-white/50",
          note.isPinned && "bg-gradient-to-br from-amber-50 to-yellow-50 border-l-4 border-l-amber-500"
        )}
        style={!note.isPinned && note.relatedEntity?.color ? { borderLeftWidth: '3px', borderLeftColor: note.relatedEntity.color } : undefined}
      >
        <div className="p-5">
          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            {note.isPinned ? (
              <div className="flex items-center gap-1 text-amber-600">
                <Pin className="w-4 h-4 fill-current" />
                <span className="text-xs font-bold uppercase tracking-wide">Pinned</span>
              </div>
            ) : (
              <div></div>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger className="flex-shrink-0">
                <div className="p-1 hover:bg-gray-100 rounded transition-colors">
                  <MoreHorizontal className="w-4 h-4 text-gray-500" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onPin && (
                  <DropdownMenuItem onClick={onPin}>
                    <Pin className="mr-2 h-4 w-4" />
                    {note.isPinned ? 'Desfijar' : 'Fijar nota'}
                  </DropdownMenuItem>
                )}
                {(onView || onEdit) && <DropdownMenuSeparator />}
                {onView && (
                  <DropdownMenuItem onClick={onView}>
                    <Eye className="mr-2 h-4 w-4" />
                    Ver detalle
                  </DropdownMenuItem>
                )}
                {onEdit && (
                  <DropdownMenuItem onClick={onEdit}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={onDelete} className="text-red-600 focus:text-red-600 focus:bg-red-50">
                      <Trash2 className="mr-2 h-4 w-4" />
                      Eliminar
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Content */}
          <div className="mb-4">
            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
              {note.content}
            </p>
          </div>

          {/* Related Entity */}
          {note.relatedEntity && (
            <div className="mb-3 pb-3 border-t border-gray-200 pt-3">
              <div 
                className="flex items-center gap-2 text-sm hover:underline cursor-pointer"
                style={{ color: note.relatedEntity.color || accentColor }}
              >
                {RelatedIcon && <RelatedIcon className="w-4 h-4" />}
                <span className="truncate">{note.relatedEntity.name}</span>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between text-xs text-gray-600">
            {note.author && (
              <div className="flex items-center gap-2">
                <Avatar className="w-5 h-5">
                  <AvatarFallback className="text-xs bg-gray-100 text-gray-700">
                    {getInitials(note.author.name)}
                  </AvatarFallback>
                </Avatar>
                <span>{note.author.name}</span>
              </div>
            )}
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              <span>{formatRelativeTime(isEdited ? note.updatedAt! : note.createdAt)}</span>
              {isEdited && <span className="text-gray-500">· Editado</span>}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default NoteCard;
